import { computed, markRaw, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import { ElMessage } from 'element-plus'
import { cloneCaptureProfile } from '@/core/capture-profile'
import {
  evaluateSessionStartBaseline,
  SessionStartExpConsensus,
  type ConfirmedSessionStartExp,
  type SessionStartIssue,
  type SessionStartBaselineEvaluation,
  type SessionStartBaselineStatus,
} from '@/core/session-start-baseline'
import { SessionEngine } from '@/core/session-engine'
import { DEFAULT_CAPTURE_FPS, migrateRecognitionFrequencies } from '@/core/recognition-settings'
import { metricKeys, type CaptureProfile, type GameStateSnapshot, type LiveRateSnapshot, type MetricKey, type RawReading, type SessionJourney, type SessionRecord, type ValidatedObservation } from '@/domain/types'
import { i18n } from '@/i18n'
import { PresetRegistry } from '@/presets/registry'
import type { GamePreset, HudLocation, PresetRecognizer } from '@/presets/types'
import { AnalysisPipeline, type PipelineResult } from '@/services/analysis-pipeline'
import {
  captureService,
  classifyCaptureFailure,
  type CaptureFailureKind,
  type CaptureMediaState,
  type CaptureStatus,
} from '@/services/capture-service'
import { db, RecoveryBuffer, saveSessionState } from '@/services/database'
import {
  ensureOcrModelsCached,
  isOcrResourceLoadError,
  type OcrCachePreparationResult,
  type OcrCacheProgress,
} from '@/services/ocr-resource-cache'
import { releaseSharedOcrEngine } from '@/services/ocr-engine'
import { pipService } from '@/services/pip-service'
import { useAppStore } from './app'

export type OcrStatus = 'idle' | 'checking' | 'downloading' | 'prepared' | 'initializing' | 'ready' | 'error'
export type OcrStage = 'idle' | 'checking' | 'detection' | 'recognition' | 'prepared' | 'initializing' | 'ready'
export type OcrErrorType = 'resource' | 'initialization' | 'unknown'
export type CaptureStartupPhase = 'idle' | 'picker' | 'selected' | 'resources' | 'worker' | 'video' | 'ready' | 'error'
export interface SessionStartProgress {
  readonly phase: 'confirming' | 'slow'
  readonly requestedAt: number
  readonly elapsedMs: number
  readonly confirmedSamples: 0 | 1 | 2
  readonly targetSamples: 2
  readonly currentMetric: 'level' | 'exp'
  readonly lastInferenceDurationMs?: number
}

const BACKGROUND_CONTINUITY_GAP_MS = 5_000
const SESSION_START_BASELINE_MAX_AGE_MS = 2_000
const SESSION_START_BASELINE_SOFT_WAIT_MS = 6_000
const SESSION_START_BASELINE_HARD_TIMEOUT_MS = 24_000
const SESSION_START_BASELINE_ABSOLUTE_TIMEOUT_MS = 60_000
const SESSION_START_RETRY_DELAY_MS = 250
const SLOW_CAPTURE_SELECTION_MS = 15_000
const CAPTURE_STARTUP_PHASE_KEY = 'maple-log:capture-startup-phase'

type SessionStartBaselineResult =
  | { readonly status: 'ready'; readonly baseline: GameStateSnapshot; readonly startedAt: number; readonly requestedAt: number }
  | { readonly status: Exclude<SessionStartBaselineStatus, 'ready'>; readonly baseline: null; readonly requestedAt: number; readonly hardTimedOut?: boolean; readonly confirmedSamples?: 0 | 1 }
  | { readonly status: 'cancelled'; readonly baseline: null; readonly requestedAt: number }

export const useLiveStore = defineStore('live', () => {
  const app = useAppStore()
  const preset = shallowRef<GamePreset | null>(null)
  const profile = shallowRef<CaptureProfile | null>(null)
  const session = shallowRef<SessionRecord | null>(null)
  const liveRates = shallowRef<LiveRateSnapshot | null>(null)
  const gameState = shallowRef<GameStateSnapshot | null>(null)
  const rawReading = shallowRef<RawReading | null>(null)
  const hudLocation = shallowRef<HudLocation | null>(null)
  const captureStatus = ref<CaptureStatus>(captureService.status)
  const captureLabel = ref('')
  const captureFailure = ref<CaptureFailureKind | null>(null)
  const captureStartupPhase = ref<CaptureStartupPhase>('idle')
  const error = ref<string | null>(null)
  const needsCalibration = ref(false)
  const ocrStatus = ref<OcrStatus>('idle')
  const ocrStage = ref<OcrStage>('idle')
  const ocrProgress = ref(0)
  const ocrCachedModels = ref(0)
  const ocrError = ref<string | null>(null)
  const ocrErrorType = ref<OcrErrorType | null>(null)
  const ocrCacheDegraded = ref(false)
  const isStarting = ref(false)
  const isEnding = ref(false)
  const startIssue = shallowRef<SessionStartIssue | null>(null)
  const sessionStartProgress = shallowRef<SessionStartProgress | null>(null)
  const journey = shallowRef<SessionJourney | null>(null)
  const video = markRaw(captureService.video)
  const recovery = markRaw(new RecoveryBuffer())
  let pipeline: AnalysisPipeline | null = null
  let sessionEngine: SessionEngine | null = null
  let calibrationMetric: MetricKey | null = null
  let captureGeneration = 0
  let ocrResourcePreparation: Promise<void> | null = null
  let ocrEngineInitialization: Promise<void> | null = null
  let retainedOcrPreparation: OcrCachePreparationResult | null = null
  let endSessionPromise: Promise<void> | null = null
  let startSessionPromise: Promise<void> | null = null
  let startBaselineController: AbortController | null = null
  let settlementController: AbortController | null = null
  let liveRateRefreshTimer = 0
  let lastLiveRateRefreshAt = 0
  let pageHiddenAt: number | null = null
  let pageWasFrozen = false
  let mediaContinuityLost = false
  let recoveringPipeline = false
  let activePipelineGeneration = -1
  let ocrLifecycleRevision = 0
  let preparedOcrFingerprint = ''
  let readyOcrFingerprint = ''
  let cacheDegradedWarningShown = false
  let latestObservation: ValidatedObservation | null = null
  let latestObservationGeneration = -1
  let initialized = false

  const isCapturing = computed(() => captureStatus.value === 'active')
  const isSelectingWindow = computed(() => captureStatus.value === 'requesting'
    || captureStatus.value === 'connecting'
    || (captureStatus.value === 'selected' && ocrStatus.value !== 'error'))
  const canSelectWindow = computed(() => Boolean(preset.value && profile.value && captureService.supported))
  const isRunning = computed(() => session.value?.status === 'running')
  const isPaused = computed(() => session.value?.status === 'paused')

  async function initialize(): Promise<void> {
    if (initialized) return
    initialized = true
    try {
      await loadPreset(app.presetId, false)
      captureService.addEventListener('status', ((event: CustomEvent<CaptureStatus>) => {
        captureStatus.value = event.detail
      }) as EventListener)
      captureService.addEventListener('ended', () => {
        void interruptCapture().catch((caught: unknown) => {
          error.value = caught instanceof Error ? caught.message : String(caught)
        })
      })
      captureService.addEventListener('media-state', ((event: CustomEvent<CaptureMediaState>) => {
        handleCaptureMediaState(event.detail)
      }) as EventListener)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      document.addEventListener('freeze', handlePageFreeze)
      document.addEventListener('resume', handlePageResume)
      window.addEventListener('pageshow', handlePageResume)
    } catch (caught) {
      initialized = false
      error.value = caught instanceof Error ? `${caught.name}: ${caught.message}` : String(caught)
      ocrStatus.value = 'error'
      ocrError.value = error.value
      ocrErrorType.value = 'unknown'
      return
    }
  }

  async function loadPreset(id: string, initializeResources = initialized): Promise<void> {
    startBaselineController?.abort()
    if (session.value && session.value.status !== 'ended' && preset.value?.id !== id) {
      throw new Error('End the active record before changing presets')
    }
    const previousPreset = preset.value
    const previousPipeline = pipeline
    ocrLifecycleRevision += 1
    ocrResourcePreparation = null
    ocrEngineInitialization = null
    previousPipeline?.stop()
    const engineRelease = previousPreset
      ? releaseSharedOcrEngine(previousPreset.remoteResources.ocrModels)
      : Promise.resolve()
    previousPipeline?.dispose()
    pipeline = null
    activePipelineGeneration = -1
    releasePreparedOcrResources()
    preparedOcrFingerprint = ''
    readyOcrFingerprint = ''
    setOcrIdle()
    latestObservation = null
    latestObservationGeneration = -1
    stopLiveRateRefresh()
    await engineRelease
    preset.value = await PresetRegistry.load(id)
    app.presetId = id
    sessionEngine = new SessionEngine(preset.value)
    journey.value = null
    liveRates.value = null
    const exactProfiles = await db.profiles.where('[presetId+presetVersion]').equals([id, preset.value.version]).toArray()
    const exact = exactProfiles.reduce<CaptureProfile | null>((latest, item) => !latest || item.updatedAt > latest.updatedAt ? item : latest, null)
    const previous = exact ? null : await db.profiles.orderBy('updatedAt').reverse().filter((item) => item.presetId === id).first()
    const migrated = previous ? preset.value.migrateProfile(previous) : null
    needsCalibration.value = Boolean(previous && !migrated)
    const selectedProfile = exact ?? migrated ?? createProfile(preset.value)
    profile.value = {
      ...selectedProfile,
      recognition: migrateRecognitionFrequencies(selectedProfile.recognition),
    }
    await db.profiles.put(profile.value)
    if (initializeResources && isCapturing.value) await initializeOcr()
  }

  async function selectWindow(): Promise<void> {
    if (isEnding.value || isSelectingWindow.value) return
    if (!canSelectWindow.value || !preset.value || !profile.value) return
    const selectionStartedAt = Date.now()
    const selection = captureService.selectWindow()
    setCaptureStartupPhase('picker')
    startBaselineController?.abort()
    sessionStartProgress.value = null
    startIssue.value = null
    error.value = null
    captureFailure.value = null
    captureGeneration += 1
    const interrupted = session.value?.status === 'running'
      ? interruptCapture().catch((caught: unknown) => {
          error.value = caught instanceof Error ? caught.message : String(caught)
        })
      : Promise.resolve()
    const engineRelease = releaseRecognitionForCaptureSelection()
    try {
      const info = await selection
      setCaptureStartupPhase('selected')
      const selectionDurationMs = Date.now() - selectionStartedAt
      if (captureService.status === 'selected') await waitForNativePickerExit()
      await interrupted
      await engineRelease
      captureFailure.value = null
      mediaContinuityLost = false
      pageWasFrozen = false
      pageHiddenAt = null
      captureLabel.value = info.label
      if (selectionDurationMs >= SLOW_CAPTURE_SELECTION_MS) {
        ElMessage({
          type: 'warning',
          message: String(i18n.global.t('live.captureSelectionSlow')),
          duration: 6_000,
          showClose: true,
        })
      }
      await initializeOcr()
    } catch (caught) {
      setCaptureStartupPhase(captureService.status === 'selected' ? 'error' : 'idle')
      const failure = classifyCaptureFailure(caught)
      captureFailure.value = failure === 'permission-denied' ? null : failure
      const messageKey = failure === 'permission-denied'
        ? 'live.capturePermissionDenied'
        : failure === 'source-unavailable'
          ? 'live.captureSourceUnavailable'
          : failure === 'timeout'
            ? 'live.captureTimeout'
            : failure === 'unsupported'
              ? 'live.captureUnsupported'
              : 'live.captureFailed'
      ElMessage({
        type: failure === 'permission-denied' ? 'warning' : 'error',
        message: String(i18n.global.t(messageKey)),
        duration: 4_500,
        showClose: true,
      })
    }
  }

  function startSession(): Promise<void> {
    if (startSessionPromise) return startSessionPromise
    if (isEnding.value || (session.value && session.value.status !== 'ended')) return Promise.resolve()
    startSessionPromise = (async () => {
      isStarting.value = true
      if (!isCapturing.value) await selectWindow()
      if (!isCapturing.value || !profile.value || !sessionEngine) return

      const baselineResult = await acquireSessionStartBaseline()
      if (baselineResult.status === 'cancelled') return
      if (baselineResult.status !== 'ready') {
        startIssue.value = sessionStartIssue(
          baselineResult.status,
          baselineResult.requestedAt,
          baselineResult.hardTimedOut ?? false,
          baselineResult.confirmedSamples,
        )
        return
      }

      const { baseline, startedAt } = baselineResult
      startIssue.value = null
      captureGeneration += 1
      session.value = sessionEngine.start(profile.value.id, startedAt, baseline, captureGeneration)
      journey.value = sessionEngine.journey()
      liveRates.value = sessionEngine.liveRates(startedAt)
      startLiveRateRefresh()
      await saveSessionState(session.value, journey.value)
      if (pipeline) {
        activePipelineGeneration = captureGeneration
        await pipeline.beginSession(video, session.value.id, baseline, captureGeneration, startedAt)
      } else {
        startPipeline(session.value.id, baseline, captureGeneration)
      }
    })().finally(() => {
      isStarting.value = false
      startSessionPromise = null
    })
    return startSessionPromise
  }

  async function pauseSession(): Promise<void> {
    if (!sessionEngine || isEnding.value) return
    const pausedAt = Date.now()
    session.value = sessionEngine.pause(pausedAt)
    journey.value = sessionEngine.journey()
    liveRates.value = sessionEngine.liveRates(pausedAt)
    stopLiveRateRefresh()
    await saveSessionState(session.value, journey.value)
  }

  async function resumeSession(): Promise<void> {
    if (!sessionEngine || isEnding.value) return
    const resumedAt = Date.now()
    captureGeneration += 1
    session.value = sessionEngine.resume(resumedAt, captureGeneration)
    journey.value = sessionEngine.journey()
    pipeline?.setCaptureGeneration(captureGeneration)
    activePipelineGeneration = pipeline ? captureGeneration : -1
    pipeline?.resetBaseline()
    session.value = sessionEngine.current(resumedAt)
    liveRates.value = sessionEngine.liveRates(resumedAt)
    startLiveRateRefresh()
    if (session.value) await saveSessionState(session.value, journey.value)
  }

  function endSession(): Promise<void> {
    if (endSessionPromise) return endSessionPromise
    if (!sessionEngine || !session.value || session.value.status === 'ended') return Promise.resolve()
    const engine = sessionEngine
    endSessionPromise = (async () => {
      isEnding.value = true
      if (session.value?.status === 'running' && isCapturing.value && pipeline) {
        settlementController = new AbortController()
        let timeoutId = 0
        const timeout = new Promise<boolean>((resolve) => {
          timeoutId = window.setTimeout(() => {
            settlementController?.abort()
            resolve(false)
          }, 2_000)
        })
        try {
          await Promise.race([
            pipeline.settle(video, ['level', 'exp'], settlementController.signal),
            timeout,
          ])
        } catch {
          // Keep all previously confirmed totals if the final sample fails.
        } finally {
          window.clearTimeout(timeoutId)
          settlementController.abort()
          settlementController = null
        }
      }

      const endedAt = Date.now()
      stopLiveRateRefresh()
      session.value = engine.end(endedAt)
      journey.value = engine.journey()
      liveRates.value = engine.liveRates(endedAt)
      await recovery.flush(session.value, journey.value, true)
    })().finally(() => {
      isEnding.value = false
      endSessionPromise = null
    })
    return endSessionPromise
  }

  async function updateProfile(next: CaptureProfile): Promise<void> {
    const previous = profile.value
    const sourceChanged = previous ? captureSourceChanged(previous, next) : true
    const adaptiveMetricToReset = previous ? changedAdaptiveMetric(previous, next) : null
    profile.value = { ...cloneCaptureProfile(next), updatedAt: Date.now() }
    needsCalibration.value = false
    pipeline?.updateProfile(profile.value)
    if (sourceChanged) {
      startBaselineController?.abort()
      captureGeneration += 1
      pipeline?.setCaptureGeneration(captureGeneration, adaptiveMetricToReset ?? undefined)
      activePipelineGeneration = pipeline ? captureGeneration : -1
      pipeline?.resetBaseline(adaptiveMetricToReset ?? undefined)
    }
    await db.profiles.put(profile.value)
  }

  function setCalibrationMetric(metric: MetricKey | null): void {
    calibrationMetric = metric
    pipeline?.setDiagnosticMetric(metric)
  }

  async function openPip(): Promise<void> {
    await pipService.open(() => ({
      session: session.value,
      state: gameState.value,
      liveRates: liveRates.value,
      canStart: isCapturing.value && ocrStatus.value === 'ready' && !isStarting.value,
      isStarting: isStarting.value,
      isEnding: isEnding.value,
    }), {
      start: startSession,
      pause: pauseSession,
      resume: resumeSession,
      end: endSession,
      heartbeat: handlePipHeartbeat,
    }, app.locale)
  }

  async function initializeOcr(): Promise<void> {
    const hasSelectedSource = captureStatus.value === 'selected' || isCapturing.value
    if (!hasSelectedSource) {
      setOcrIdle()
      return
    }
    setCaptureStartupPhase('resources')
    await prepareOcrResources()
    if ((captureStatus.value !== 'selected' && !isCapturing.value) || ocrStatus.value === 'error') {
      if (ocrStatus.value === 'error') setCaptureStartupPhase('error')
      return
    }
    setCaptureStartupPhase('worker')
    if (captureStatus.value === 'selected') await yieldToMainThread()
    await initializeOcrEngine()
    if (ocrStatus.value !== 'ready') {
      if (ocrError.value) setCaptureStartupPhase('error')
      return
    }
    if (captureStatus.value === 'selected') {
      setCaptureStartupPhase('video')
      try {
        const info = await captureService.connectSelectedWindow()
        captureLabel.value = info.label
        if (profile.value) {
          profile.value = {
            ...profile.value,
            sourceWidth: info.width || profile.value.sourceWidth,
            sourceHeight: info.height || profile.value.sourceHeight,
            updatedAt: Date.now(),
          }
          await db.profiles.put(profile.value)
        }
      } catch (caught) {
        setCaptureStartupPhase('error')
        captureFailure.value = classifyCaptureFailure(caught)
        ocrStatus.value = 'error'
        ocrError.value = caught instanceof Error ? caught.message : String(caught)
        ocrErrorType.value = 'unknown'
        return
      }
    }
    setCaptureStartupPhase('ready')
    await activateCaptureRecognition()
  }

  async function prepareOcrResources(): Promise<void> {
    if (!preset.value || !profile.value) return
    if (ocrResourcePreparation) return ocrResourcePreparation
    const targetPreset = preset.value
    const fingerprint = ocrFingerprint(targetPreset)
    if (readyOcrFingerprint === fingerprint) {
      ocrStatus.value = 'ready'
      ocrStage.value = 'ready'
      ocrProgress.value = 100
      return
    }
    if (preparedOcrFingerprint === fingerprint && retainedOcrPreparation) {
      if (ocrStatus.value !== 'initializing') {
        ocrStatus.value = 'prepared'
        ocrStage.value = 'prepared'
        ocrProgress.value = Math.max(80, ocrProgress.value)
      }
      return
    }

    const lifecycleRevision = ocrLifecycleRevision
    let preparationTask!: Promise<void>
    preparationTask = (async () => {
      releasePreparedOcrResources()
      ocrStatus.value = 'checking'
      ocrStage.value = 'checking'
      ocrProgress.value = 4
      ocrCachedModels.value = 0
      ocrError.value = null
      ocrErrorType.value = null
      ocrCacheDegraded.value = false
      const cached = new Set<string>()
      let pendingProgress: OcrCacheProgress | null = null
      let progressFrame = 0
      const applyProgress = () => {
        progressFrame = 0
        const progress = pendingProgress
        pendingProgress = null
        if (!progress || lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) return
        ocrStatus.value = 'downloading'
        ocrStage.value = progress.model
        ocrProgress.value = Math.max(5, Math.round(5 + progress.progress * 70))
        ocrCachedModels.value = cached.size
      }
      try {
        const preparation = await ensureOcrModelsCached(targetPreset.remoteResources.ocrModels, (progress) => {
          if (progress.fromCache) cached.add(progress.model)
          pendingProgress = progress
          if (!progressFrame) progressFrame = requestAnimationFrame(applyProgress)
        })
        if (lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) {
          if (progressFrame) cancelAnimationFrame(progressFrame)
          pendingProgress = null
          preparation.releaseTemporaryResources()
          return
        }
        retainedOcrPreparation = preparation
        preparedOcrFingerprint = fingerprint
        ocrCacheDegraded.value = preparation.cacheDegraded
        if (progressFrame) cancelAnimationFrame(progressFrame)
        applyProgress()
        ocrStatus.value = 'prepared'
        ocrStage.value = 'prepared'
        ocrProgress.value = 80
      } catch (caught) {
        if (progressFrame) cancelAnimationFrame(progressFrame)
        pendingProgress = null
        if (lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) return
        preparedOcrFingerprint = ''
        ocrStatus.value = 'error'
        ocrError.value = caught instanceof Error ? caught.message : String(caught)
        ocrErrorType.value = isOcrResourceLoadError(caught) ? 'resource' : 'unknown'
      }
    })().finally(() => {
      if (ocrResourcePreparation === preparationTask) ocrResourcePreparation = null
    })
    ocrResourcePreparation = preparationTask
    return preparationTask
  }

  async function initializeOcrEngine(): Promise<void> {
    if (!preset.value || !profile.value) return
    if (ocrEngineInitialization) return ocrEngineInitialization
    const targetPreset = preset.value
    const targetProfile = profile.value
    const fingerprint = ocrFingerprint(targetPreset)
    if (readyOcrFingerprint === fingerprint) {
      ocrStatus.value = 'ready'
      ocrStage.value = 'ready'
      ocrProgress.value = 100
      return
    }
    if (preparedOcrFingerprint !== fingerprint) return

    const lifecycleRevision = ocrLifecycleRevision
    let initializationTask!: Promise<void>
    initializationTask = (async () => {
      let recognizer: PresetRecognizer | null = null
      ocrStatus.value = 'initializing'
      ocrStage.value = 'initializing'
      ocrProgress.value = 82
      ocrError.value = null
      ocrErrorType.value = null
      try {
        recognizer = targetPreset.createRecognizer(targetProfile)
        await recognizer.warmup()
        if (lifecycleRevision !== ocrLifecycleRevision
          || preset.value !== targetPreset
          || ocrFingerprint(targetPreset) !== fingerprint) return
        readyOcrFingerprint = fingerprint
        ocrStage.value = 'ready'
        ocrStatus.value = 'ready'
        ocrProgress.value = 100
        if (retainedOcrPreparation?.usedTemporaryResources && !cacheDegradedWarningShown) {
          cacheDegradedWarningShown = true
          ElMessage({
            type: 'warning',
            message: String(i18n.global.t('startup.cacheDegradedWarning')),
            duration: 5_000,
            showClose: true,
          })
        }
        releasePreparedOcrResources()
      } catch (caught) {
        if (lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) return
        ocrStatus.value = 'error'
        ocrError.value = caught instanceof Error ? caught.message : String(caught)
        ocrErrorType.value = 'initialization'
      } finally {
        recognizer?.dispose()
      }
    })().finally(() => {
      if (ocrEngineInitialization === initializationTask) ocrEngineInitialization = null
    })
    ocrEngineInitialization = initializationTask
    return initializationTask
  }

  async function activateCaptureRecognition(): Promise<void> {
    if (!isCapturing.value
      || ocrStatus.value !== 'ready'
      || !preset.value
      || !profile.value
      || activePipelineGeneration === captureGeneration) return

    startPipeline(session.value?.id ?? 'preview', null, captureGeneration)
    if (session.value?.status !== 'interrupted' || !sessionEngine) return

    const resumedAt = Date.now()
    sessionEngine.resume(resumedAt, captureGeneration)
    journey.value = sessionEngine.journey()
    pipeline?.resetBaseline()
    session.value = sessionEngine.current(resumedAt)
    liveRates.value = sessionEngine.liveRates(resumedAt)
    startLiveRateRefresh()
    await persistSession()
  }

  function releasePreparedOcrResources(): void {
    retainedOcrPreparation?.releaseTemporaryResources()
    retainedOcrPreparation = null
  }

  function releaseRecognitionForCaptureSelection(): Promise<void> {
    const currentPreset = preset.value
    const currentPipeline = pipeline
    ocrLifecycleRevision += 1
    ocrResourcePreparation = null
    ocrEngineInitialization = null
    currentPipeline?.stop()
    const engineRelease = currentPreset
      ? releaseSharedOcrEngine(currentPreset.remoteResources.ocrModels)
      : Promise.resolve()
    currentPipeline?.dispose()
    pipeline = null
    activePipelineGeneration = -1
    releasePreparedOcrResources()
    preparedOcrFingerprint = ''
    readyOcrFingerprint = ''
    setOcrIdle()
    return engineRelease
  }

  function setOcrIdle(): void {
    ocrStatus.value = 'idle'
    ocrStage.value = 'idle'
    ocrProgress.value = 0
    ocrCachedModels.value = 0
    ocrError.value = null
    ocrErrorType.value = null
    ocrCacheDegraded.value = false
  }

  async function interruptCapture(): Promise<void> {
    if (isEnding.value) {
      settlementController?.abort()
      return
    }
    startBaselineController?.abort()
    if (!sessionEngine || session.value?.status !== 'running') return
    pipeline?.stop()
    activePipelineGeneration = -1
    stopLiveRateRefresh()
    const interruptedAt = Date.now()
    session.value = sessionEngine.interrupt(interruptedAt)
    journey.value = sessionEngine.journey()
    liveRates.value = sessionEngine.liveRates(interruptedAt)
    await persistSession()
  }

  async function persistSession(): Promise<void> {
    if (!session.value) return
    await saveSessionState(session.value, journey.value)
  }

  function startPipeline(sessionId: string, baseline: GameStateSnapshot | null = null, generation = captureGeneration): void {
    if (!preset.value || !profile.value) return
    pipeline?.dispose()
    let nextPipeline!: AnalysisPipeline
    nextPipeline = markRaw(new AnalysisPipeline(
      preset.value,
      profile.value,
      onPipelineResult,
      () => recoverPipeline(nextPipeline),
    ))
    pipeline = nextPipeline
    activePipelineGeneration = generation
    nextPipeline.setDiagnosticMetric(calibrationMetric)
    nextPipeline.start(video, sessionId, baseline, generation)
  }

  function startLiveRateRefresh(): void {
    stopLiveRateRefresh()
    lastLiveRateRefreshAt = 0
    liveRateRefreshTimer = window.setInterval(() => {
      refreshLiveRates()
    }, 1_000)
  }

  function stopLiveRateRefresh(): void {
    if (!liveRateRefreshTimer) return
    window.clearInterval(liveRateRefreshTimer)
    liveRateRefreshTimer = 0
  }

  function refreshLiveRates(now = Date.now()): void {
    if (session.value?.status !== 'running' || !sessionEngine) return
    if (now - lastLiveRateRefreshAt < 1_000) return
    lastLiveRateRefreshAt = now
    session.value = sessionEngine.current(now)
    liveRates.value = sessionEngine.liveRates(now)
  }

  function handlePipHeartbeat(): void {
    if (isSelectingWindow.value || ocrStatus.value !== 'ready') return
    pipeline?.pulse()
    refreshLiveRates()
  }

  function recoverPipeline(expectedPipeline: AnalysisPipeline | null = pipeline): void {
    if (recoveringPipeline
      || !expectedPipeline
      || pipeline !== expectedPipeline
      || !isCapturing.value
      || !preset.value
      || !profile.value
      || isStarting.value
      || isEnding.value) return

    recoveringPipeline = true
    try {
      captureGeneration += 1
      mediaContinuityLost = false
      pageHiddenAt = null
      const sessionId = session.value?.id ?? 'preview'
      startPipeline(sessionId, gameState.value, captureGeneration)
      pipeline?.pulse(true)
    } finally {
      recoveringPipeline = false
    }
  }

  function handleCaptureMediaState(state: CaptureMediaState): void {
    if (state === 'waiting' || state === 'stalled' || state === 'paused' || state === 'muted' || state === 'idle') {
      mediaContinuityLost = true
      startBaselineController?.abort()
      return
    }
    if (state !== 'playing' && state !== 'unmuted') return

    const shouldRebaseline = mediaContinuityLost
    mediaContinuityLost = false
    if (shouldRebaseline) recoverPipeline()
    else pipeline?.pulse(true)
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      pageHiddenAt ??= Date.now()
      return
    }
    handlePageResume()
  }

  function handlePageFreeze(): void {
    pageWasFrozen = true
    startBaselineController?.abort()
    if (session.value && session.value.status !== 'ended') {
      void persistSession().catch(() => undefined)
    }
  }

  function handlePageResume(): void {
    if (!isCapturing.value || !pipeline) {
      pageWasFrozen = false
      if (document.visibilityState === 'visible') pageHiddenAt = null
      return
    }
    if (pageWasFrozen) {
      pageWasFrozen = false
      pageHiddenAt = null
      recoverPipeline()
      return
    }

    const now = Date.now()
    const health = pipeline.health()
    const missedBackgroundFrames = pageHiddenAt !== null
      && now - pageHiddenAt >= BACKGROUND_CONTINUITY_GAP_MS
      && (health?.lastSuccessfulAt === null
        || health?.lastSuccessfulAt === undefined
        || now - health.lastSuccessfulAt >= BACKGROUND_CONTINUITY_GAP_MS)
    pageHiddenAt = null
    if (missedBackgroundFrames) recoverPipeline()
    else pipeline.pulse(true)
  }

  function onPipelineResult(result: PipelineResult): void {
    if (result.captureGeneration !== captureGeneration) return
    latestObservation = result.observation
    latestObservationGeneration = result.captureGeneration
    gameState.value = result.state
    hudLocation.value = result.hud
    if (session.value?.status !== 'running' || !sessionEngine) {
      rawReading.value = result.raw
      return
    }
    const ingested = sessionEngine.ingestWithAccounting(result.state, result.events, result.observation, result.captureGeneration)
    const displayExp = sessionEngine.expDisplayStatus()
    const expDiagnostic = result.raw.diagnostics.metrics?.exp
    rawReading.value = expDiagnostic
      ? {
          ...result.raw,
          diagnostics: {
            ...result.raw.diagnostics,
            metrics: {
              ...result.raw.diagnostics.metrics,
              exp: {
                ...expDiagnostic,
                ...(displayExp.candidateValue === null ? {} : { candidateValue: {
                  absolute: displayExp.candidateValue,
                  ratio: result.raw.expExactCandidate?.value.ratio ?? null,
                } }),
                ...(displayExp.acceptedValue === null ? {} : { acceptedValue: {
                  absolute: displayExp.acceptedValue,
                  ratio: result.raw.expExactCandidate?.value.ratio ?? result.raw.exp.value?.ratio ?? null,
                } }),
              },
            },
          },
        }
      : result.raw
    session.value = ingested.record
    journey.value = sessionEngine.journey()
    liveRates.value = sessionEngine.liveRates(result.state.capturedAt)
    void recovery.flush(session.value, journey.value).catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }

  async function acquireSessionStartBaseline(): Promise<SessionStartBaselineResult> {
    const requestedAt = Date.now()
    const existing = currentSessionStartBaseline(requestedAt, SESSION_START_BASELINE_MAX_AGE_MS)
    if (existing.status === 'ready') {
      return { status: 'ready', baseline: existing.baseline, startedAt: requestedAt, requestedAt }
    }
    const targetPipeline = pipeline
    const targetGeneration = captureGeneration
    if (!targetPipeline || !isCapturing.value) return { status: 'capture-unavailable', baseline: null, requestedAt }

    const reusableCandidate = latestObservationGeneration === targetGeneration
      && latestObservation?.expExactCandidate
      && requestedAt >= latestObservation.expExactCandidate.sourceAt
      && requestedAt - latestObservation.expExactCandidate.sourceAt <= SESSION_START_BASELINE_MAX_AGE_MS
      && latestObservation.expExactCandidate.levelSourceAt !== null
      && requestedAt >= latestObservation.expExactCandidate.levelSourceAt
      && requestedAt - latestObservation.expExactCandidate.levelSourceAt <= SESSION_START_BASELINE_MAX_AGE_MS
      ? latestObservation.expExactCandidate
      : undefined

    const controller = new AbortController()
    startBaselineController = controller
    const consensus = new SessionStartExpConsensus(
      requestedAt,
      profile.value?.recognition.minConfidence ?? 0.62,
      reusableCandidate,
    )
    sessionStartProgress.value = {
      phase: 'confirming',
      requestedAt,
      elapsedMs: 0,
      confirmedSamples: consensus.sampleCount,
      targetSamples: 2,
      currentMetric: existing.status === 'level-unconfirmed' ? 'level' : 'exp',
    }
    let latest: SessionStartBaselineEvaluation = existing
    let completedFreshSample = false
    let latestCandidateAt: number | null = reusableCandidate?.sourceAt ?? null
    let latestCandidateAbsolute: number | null = reusableCandidate?.value.absolute ?? null
    let forceLevelSample = reusableCandidate === undefined
    let softWaitId = 0
    let noProgressTimeoutId = 0
    const absoluteTimeoutId = window.setTimeout(() => controller.abort('absolute-timeout'), SESSION_START_BASELINE_ABSOLUTE_TIMEOUT_MS)
    const cancelled = new Promise<null>((resolve) => {
      controller.signal.addEventListener('abort', () => resolve(null), { once: true })
    })

    const updateProgress = (currentMetric: 'level' | 'exp', lastInferenceDurationMs?: number) => {
      const previous = sessionStartProgress.value
      if (!previous || previous.requestedAt !== requestedAt) return
      sessionStartProgress.value = {
        ...previous,
        elapsedMs: Date.now() - requestedAt,
        confirmedSamples: consensus.sampleCount,
        currentMetric,
        ...(lastInferenceDurationMs === undefined ? {} : { lastInferenceDurationMs }),
      }
    }

    const armNoProgressTimeout = () => {
      window.clearTimeout(noProgressTimeoutId)
      noProgressTimeoutId = window.setTimeout(() => controller.abort('no-progress-timeout'), SESSION_START_BASELINE_HARD_TIMEOUT_MS)
    }

    const collect = async (): Promise<SessionStartBaselineResult | null> => {
      await targetPipeline.prepareForSessionStart(controller.signal)
      if (controller.signal.aborted) return null
      softWaitId = window.setTimeout(() => {
        if (startBaselineController === controller && !controller.signal.aborted) {
          const previous = sessionStartProgress.value
          sessionStartProgress.value = {
            phase: 'slow',
            requestedAt,
            elapsedMs: Date.now() - requestedAt,
            confirmedSamples: consensus.sampleCount,
            targetSamples: 2,
            currentMetric: previous?.currentMetric ?? 'exp',
            ...(previous?.lastInferenceDurationMs === undefined
              ? {}
              : { lastInferenceDurationMs: previous.lastInferenceDurationMs }),
          }
        }
      }, SESSION_START_BASELINE_SOFT_WAIT_MS)
      armNoProgressTimeout()

      while (!controller.signal.aborted) {
        if (pipeline !== targetPipeline || captureGeneration !== targetGeneration || !isCapturing.value) {
          return { status: 'capture-unavailable', baseline: null, requestedAt }
        }

        const needsLevel = forceLevelSample || latest.status === 'level-unconfirmed'
        const requestedMetrics: readonly MetricKey[] = needsLevel ? ['level', 'exp'] : ['exp']
        updateProgress(needsLevel ? 'level' : 'exp')
        try {
          const sampled = await targetPipeline.sampleForSessionStart(video, requestedMetrics, controller.signal)
          completedFreshSample ||= sampled
        } catch {
          if (controller.signal.aborted) break
        }
        if (pipeline !== targetPipeline || captureGeneration !== targetGeneration || !isCapturing.value) {
          return { status: 'capture-unavailable', baseline: null, requestedAt }
        }

        latest = currentSessionStartBaseline(Date.now(), null, requestedAt)
        const candidate = latestObservationGeneration === targetGeneration
          ? latestObservation?.expExactCandidate
          : undefined
        const previousCandidateAt = latestCandidateAt
        const previousCandidateAbsolute = latestCandidateAbsolute
        const confirmation = consensus.observe(candidate)
        if (candidate && candidate.sourceAt >= requestedAt && candidate.sourceAt !== previousCandidateAt) {
          latestCandidateAt = candidate.sourceAt
          latestCandidateAbsolute = candidate.value.absolute
          armNoProgressTimeout()
        }
        const duration = latestObservation?.diagnostics.metrics?.[needsLevel ? 'level' : 'exp']?.durationMs
        updateProgress(confirmation.sampleCount > 0 ? 'exp' : needsLevel ? 'level' : 'exp', duration)
        if (confirmation.status === 'ready' && confirmation.confirmed) {
          const baseline = confirmedSessionStartBaseline(confirmation.confirmed, requestedAt)
          if (baseline) {
            return { status: 'ready', baseline, startedAt: confirmation.confirmed.sourceAt, requestedAt }
          }
        }

        forceLevelSample = candidate?.level === null
          || (previousCandidateAt !== null
            && candidate !== undefined
            && candidate.sourceAt !== previousCandidateAt
            && previousCandidateAbsolute !== null
            && candidate.value.absolute < previousCandidateAbsolute)

        await waitForNextSessionStartFrame(controller.signal)
      }
      return null
    }

    let result: SessionStartBaselineResult | null = null
    try {
      result = await Promise.race([collect(), cancelled])
    } finally {
      window.clearTimeout(softWaitId)
      window.clearTimeout(noProgressTimeoutId)
      window.clearTimeout(absoluteTimeoutId)
      const abortReason = controller.signal.reason
      if (!controller.signal.aborted) controller.abort('complete')
      if (startBaselineController === controller) startBaselineController = null
      if (sessionStartProgress.value?.requestedAt === requestedAt) sessionStartProgress.value = null

      if (!result && abortReason === 'user') {
        result = { status: 'cancelled', baseline: null, requestedAt }
      }
    }

    if (!result) {
      const hardTimedOut = controller.signal.reason === 'no-progress-timeout'
        || controller.signal.reason === 'absolute-timeout'
      const captureUnavailable = pipeline !== targetPipeline
        || captureGeneration !== targetGeneration
        || !isCapturing.value
        || mediaContinuityLost
        || pageWasFrozen
      result = {
        status: captureUnavailable
          ? 'capture-unavailable'
          : consensus.sampleCount === 1
            ? 'exp-confirming'
          : !completedFreshSample || latest.status === 'capture-unavailable'
            ? 'stale'
            : latest.status,
        baseline: null,
        requestedAt,
        ...(hardTimedOut ? { hardTimedOut: true } : {}),
        confirmedSamples: consensus.sampleCount === 1 ? 1 : 0,
      }
    }
    if (result.status !== 'ready'
      && pipeline === targetPipeline
      && isCapturing.value
      && !mediaContinuityLost
      && !pageWasFrozen) {
      targetPipeline.resume(video)
      targetPipeline.pulse(true)
    }
    return result
  }

  function confirmedSessionStartBaseline(
    confirmed: ConfirmedSessionStartExp,
    requestedAt: number,
  ): GameStateSnapshot | null {
    const state = gameState.value
    if (!state
      || latestObservationGeneration !== captureGeneration
      || confirmed.sourceAt < requestedAt) return null
    const observation = latestObservation
    const mesosFresh = Boolean(
      observation?.accepted.mesos
      && observation.mesos.value !== null
      && state.mesos === observation.mesos.value
      && state.observedAt?.mesos === observation.mesos.capturedAt
      && confirmed.sourceAt >= observation.mesos.capturedAt
      && confirmed.sourceAt - observation.mesos.capturedAt <= SESSION_START_BASELINE_MAX_AGE_MS,
    )
    return {
      ...state,
      capturedAt: confirmed.sourceAt,
      level: confirmed.level,
      exp: confirmed.value,
      mesos: mesosFresh ? state.mesos : null,
      observedAt: {
        hp: state.observedAt?.hp ?? null,
        mp: state.observedAt?.mp ?? null,
        mapName: state.observedAt?.mapName ?? null,
        level: confirmed.levelSourceAt,
        exp: confirmed.sourceAt,
        mesos: mesosFresh ? observation!.mesos.capturedAt : null,
      },
      confidence: {
        ...state.confidence,
        exp: confirmed.confidence,
      },
    }
  }

  function currentSessionStartBaseline(
    now: number,
    maximumAgeMs: number | null,
    minimumCapturedAt?: number,
  ): SessionStartBaselineEvaluation {
    if (!preset.value) {
      return { status: 'capture-unavailable' as const, baseline: null, sourceAt: null }
    }
    return evaluateSessionStartBaseline(gameState.value, latestObservation, {
      now,
      captureGeneration,
      observationGeneration: latestObservationGeneration,
      maximumAgeMs,
      ...(minimumCapturedAt === undefined ? {} : { minimumCapturedAt }),
      mesosMaximumAgeMs: SESSION_START_BASELINE_MAX_AGE_MS,
    })
  }

  function waitForNextSessionStartFrame(signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
      let settled = false
      let callbackId: number | null = null
      const timeoutId = window.setTimeout(finish, SESSION_START_RETRY_DELAY_MS)
      const onAbort = () => finish()

      function finish(): void {
        if (settled) return
        settled = true
        window.clearTimeout(timeoutId)
        if (callbackId !== null) video.cancelVideoFrameCallback?.(callbackId)
        signal.removeEventListener('abort', onAbort)
        resolve()
      }

      if (signal.aborted) {
        finish()
        return
      }
      signal.addEventListener('abort', onAbort, { once: true })
      if (video.requestVideoFrameCallback) {
        callbackId = video.requestVideoFrameCallback(() => finish())
      }
    })
  }

  function sessionStartIssue(
    status: Exclude<SessionStartBaselineStatus, 'ready'>,
    requestedAt: number,
    hardTimedOut = false,
    confirmedSamples: 0 | 1 = 0,
  ): SessionStartIssue {
    const metric = status === 'level-unconfirmed'
      ? 'level'
      : status === 'exp-incomplete' || status === 'exp-confirming' || status === 'exp-conflict'
        ? 'exp'
        : null
    const diagnostic = metric ? latestObservation?.diagnostics.metrics?.[metric] : undefined
    const freshDiagnostic = diagnostic?.lastAttemptAt !== undefined && diagnostic.lastAttemptAt >= requestedAt
      ? diagnostic
      : undefined
    const reason: SessionStartIssue['reason'] = status === 'capture-unavailable'
      ? 'capture-unavailable'
      : status === 'stale'
        ? hardTimedOut ? 'unresponsive' : 'slow'
        : status === 'exp-incomplete'
          ? 'incomplete'
          : status === 'exp-conflict'
            ? 'conflict'
            : freshDiagnostic?.failureReason === 'parse-failed'
              && metric === 'level'
              && /LV/i.test(freshDiagnostic.rawText ?? '')
              ? 'mixed-content'
              : freshDiagnostic?.failureReason ?? 'confirming'
    const candidate = metric === 'level' && typeof freshDiagnostic?.value === 'number'
      ? freshDiagnostic.value
      : undefined
    return {
      metric,
      reason,
      requestedAt,
      ...(typeof candidate === 'number' ? { candidate } : {}),
      ...(freshDiagnostic?.rawText ? { rawText: freshDiagnostic.rawText } : {}),
      ...(freshDiagnostic?.selectedText ? { selectedText: freshDiagnostic.selectedText } : {}),
      ...(freshDiagnostic?.confidence !== undefined ? { confidence: freshDiagnostic.confidence } : {}),
      ...(diagnostic?.roi ? { roi: diagnostic.roi } : {}),
      ...(freshDiagnostic?.recognitionPath ? { recognitionPath: freshDiagnostic.recognitionPath } : {}),
      confirmedSamples,
    }
  }

  function dismissStartIssue(): void {
    startIssue.value = null
  }

  function cancelSessionStart(): void {
    if (!startBaselineController || startBaselineController.signal.aborted) return
    startBaselineController.abort('user')
    sessionStartProgress.value = null
  }

  function setCaptureStartupPhase(phase: CaptureStartupPhase): void {
    captureStartupPhase.value = phase
    try {
      localStorage.setItem(CAPTURE_STARTUP_PHASE_KEY, JSON.stringify({ phase, recordedAt: Date.now() }))
    } catch {
      // Startup diagnostics are optional and never block capture.
    }
  }

  async function waitForNativePickerExit(): Promise<void> {
    await nextAnimationFrame()
    await nextAnimationFrame()
    await new Promise<void>((resolve) => window.setTimeout(resolve, 100))
  }

  function nextAnimationFrame(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve())
      else window.setTimeout(resolve, 0)
    })
  }

  function yieldToMainThread(): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, 0))
  }

  function createProfile(gamePreset: GamePreset): CaptureProfile {
    const now = Date.now()
    return {
      id: crypto.randomUUID(),
      name: 'Default',
      presetId: gamePreset.id,
      presetVersion: gamePreset.version,
      sourceWidth: 1368,
      sourceHeight: 800,
      hudRect: gamePreset.hud.fallback,
      roiOverrides: {},
      colorCalibration: {},
      recognition: migrateRecognitionFrequencies({
        captureFps: DEFAULT_CAPTURE_FPS,
        minConfidence: 0.62,
        ocrChangeThreshold: 0.025,
      }),
      createdAt: now,
      updatedAt: now,
    }
  }

  function ocrFingerprint(gamePreset: GamePreset): string {
    const models = gamePreset.remoteResources.ocrModels
    return `${models.detection.url}@${models.detection.version}|${models.recognition.url}@${models.recognition.version}`
  }

  function captureSourceChanged(previous: CaptureProfile, next: CaptureProfile): boolean {
    return previous.sourceWidth !== next.sourceWidth
      || previous.sourceHeight !== next.sourceHeight
      || JSON.stringify(previous.hudRect) !== JSON.stringify(next.hudRect)
      || JSON.stringify(previous.roiOverrides) !== JSON.stringify(next.roiOverrides)
      || JSON.stringify(previous.colorCalibration) !== JSON.stringify(next.colorCalibration)
      || previous.recognition.minConfidence !== next.recognition.minConfidence
      || previous.recognition.ocrChangeThreshold !== next.recognition.ocrChangeThreshold
  }

  function changedAdaptiveMetric(previous: CaptureProfile, next: CaptureProfile): MetricKey | null {
    const hasNonRoiChange = previous.sourceWidth !== next.sourceWidth
      || previous.sourceHeight !== next.sourceHeight
      || JSON.stringify(previous.hudRect) !== JSON.stringify(next.hudRect)
      || JSON.stringify(previous.colorCalibration) !== JSON.stringify(next.colorCalibration)
      || previous.recognition.minConfidence !== next.recognition.minConfidence
      || previous.recognition.ocrChangeThreshold !== next.recognition.ocrChangeThreshold
    if (hasNonRoiChange) return null
    const changed = metricKeys.filter((metric) => JSON.stringify(previous.roiOverrides[metric]) !== JSON.stringify(next.roiOverrides[metric]))
    return changed.length === 1 ? changed[0] ?? null : null
  }

  return {
    preset, profile, session, liveRates, gameState, rawReading, hudLocation, captureStatus, captureLabel, captureFailure, captureStartupPhase, error, needsCalibration,
    ocrStatus, ocrStage, ocrProgress, ocrCachedModels, ocrError, ocrErrorType, ocrCacheDegraded, journey, video, startIssue, sessionStartProgress,
    isCapturing, isSelectingWindow, canSelectWindow, isRunning, isPaused, isStarting, isEnding, pipSupported: pipService.supported,
    initialize, loadPreset, selectWindow, startSession, pauseSession, resumeSession, endSession, updateProfile, setCalibrationMetric, openPip, initializeOcr, dismissStartIssue, cancelSessionStart,
  }
})
